import * as React from "react";
import { useState, useEffect } from "react";
import { fetchAIUsageStats, updateOverageLimit, createSetupCheckout, AIUsageStats } from "../../services/aiUsageService";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Clock, ShieldAlert, Loader2, AlertCircle } from "lucide-react";

interface AIUsagePanelProps {
  isCollapsed?: boolean;
}

function formatDateShort(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatCurrency(amount: number, currency: string = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

const AIUsagePanel: React.FC<AIUsagePanelProps> = ({ isCollapsed = false }) => {
  const [usageStats, setUsageStats] = useState<AIUsageStats | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [limitInputValue, setLimitInputValue] = useState<string>('');
  const [isUpdatingLimit, setIsUpdatingLimit] = useState(false);
  const [activeUsageTab, setActiveUsageTab] = useState<'workpills' | 'projects'>('workpills');

  const loadUsageStats = async () => {
    setLoadingUsage(true);
    setUsageError(null);
    try {
      const stats = await fetchAIUsageStats();
      setUsageStats(stats);
      if (stats.overage_limit !== undefined) {
        setLimitInputValue(stats.overage_limit.toString());
      }
    } catch (error: any) {
      console.error('Error loading usage stats:', error);
      setUsageError(error?.message || 'Error loading usage statistics');
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    loadUsageStats();
  }, []);

  const handleUpdateLimit = async () => {
    const val = parseFloat(limitInputValue);
    if (isNaN(val) || val < 0) {
      setUsageError("Please enter a valid positive number");
      return;
    }

    if (usageStats?.subscription_status === 'past_due') {
      setUsageError("Please resolve your pending payment before changing your overage limit.");
      return;
    }

    const isFreePlan = usageStats?.plan_name?.toLowerCase().includes('free');
    const hasPaymentMethod = !!usageStats?.has_payment_method;
    
    if (val > 0 && isFreePlan && !hasPaymentMethod) {
      try {
        const checkoutUrl = await createSetupCheckout(window.location.href);
        window.location.href = checkoutUrl;
        return;
      } catch (error: any) {
        setUsageError(error.message || "Error creating setup session");
        return;
      }
    }

    setIsUpdatingLimit(true);
    try {
      await updateOverageLimit(val);
      await loadUsageStats();
    } catch (error: any) {
      setUsageError(error.message || "Error updating limit");
    } finally {
      setIsUpdatingLimit(false);
    }
  };

  const currency = usageStats?.currency || 'USD';
  const monthlyLimit = usageStats?.limit ?? 0;
  const isMonthlyUnlimited = monthlyLimit <= 0;
  
  const subscriptionBalance = usageStats?.subscription_balance || 0;
  const overageLimit = usageStats?.overage_limit || 0;
  const currentOverageSpend = usageStats?.current_overage_spend || 0;
  
  const planLimit = monthlyLimit > 0 ? monthlyLimit : 0;
  const planRemaining = subscriptionBalance > 0 ? subscriptionBalance : 0;
  const planUsed = Math.max(0, planLimit - planRemaining);
  const planUsagePercentage = planLimit > 0 ? Math.min(100, (planUsed / planLimit) * 100) : 0;
  
  const overageSpent = currentOverageSpend || 0;
  const overagePercentage = overageLimit > 0 ? Math.min(100, (overageSpent / overageLimit) * 100) : 0;

  if (loadingUsage && !usageStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 w-full font-sans max-w-4xl mx-auto">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Usage & Billing</h2>
        <p className="text-sm text-slate-500">Manage your AI consumption and spending limits.</p>
      </div>

      {usageError && (
        <div className="p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {usageError}
        </div>
      )}

      <div className={cn("grid gap-6", isCollapsed ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2")}>
        {/* Monthly Usage Card */}
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Zap size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Monthly Usage</h3>
                <p className="text-xs text-slate-500">Plan credits consumption</p>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-end mb-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    {usageStats?.plan_name || 'Current'} Plan
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(planUsed, currency)} 
                    <span className="text-sm font-normal text-slate-400 ml-1">
                      / {isMonthlyUnlimited ? 'Unlimited' : formatCurrency(planLimit, currency)}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="w-full bg-slate-100 rounded-full h-2.5 mb-2">
                <div 
                  className={cn(
                    "h-2.5 rounded-full transition-all",
                    isMonthlyUnlimited ? 'bg-blue-500' : 
                    planUsagePercentage >= 90 ? 'bg-red-500' : 
                    planUsagePercentage >= 75 ? 'bg-amber-500' : 'bg-blue-600'
                  )}
                  style={{ width: isMonthlyUnlimited ? '100%' : `${planUsagePercentage}%` }}
                />
              </div>
              {!isMonthlyUnlimited && (
                <p className="text-xs text-slate-500">
                  {formatCurrency(planRemaining, currency)} remaining in current period.
                </p>
              )}
            </div>

            {/* Overage Section */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert size={14} className="text-slate-600" />
                  <Label className="text-xs font-semibold text-slate-900">Overage Spending</Label>
                </div>
                {overagePercentage > 80 && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-500 animate-pulse">
                    Near Limit
                  </span>
                )}
              </div>

              {overageLimit > 0 ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-[11px] font-medium">
                    <span className="text-slate-700">Extra Limit</span>
                    <span className="text-slate-900">
                      {formatCurrency(overageSpent, currency)} / {formatCurrency(overageLimit, currency)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5">
                    <div 
                      className={cn(
                        "h-1.5 rounded-full transition-all",
                        overagePercentage > 90 ? 'bg-red-500' : 'bg-amber-500'
                      )}
                      style={{ width: `${overagePercentage}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">
                  Set an overage limit to continue using AI services after plan credits are exhausted.
                </p>
              )}

              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="5"
                      className="pl-6 h-8 text-xs bg-white border-slate-200 focus:border-slate-400"
                      placeholder="0.00"
                      value={limitInputValue}
                      onChange={(e) => setLimitInputValue(e.target.value)}
                    />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={handleUpdateLimit}
                    disabled={
                      isUpdatingLimit || 
                      limitInputValue === usageStats?.overage_limit?.toString() ||
                      usageStats?.subscription_status === 'past_due'
                    }
                    className="h-8 px-3 text-xs font-medium"
                  >
                    {isUpdatingLimit ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Limit'}
                  </Button>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-2">
                  {usageStats?.plan_name?.toLowerCase().includes('free') && !usageStats?.has_payment_method && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-tight flex items-center gap-1">
                      <AlertCircle size={10} /> Card Required
                    </span>
                  )}
                  {usageStats?.subscription_status === 'past_due' && (
                    <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-tight flex items-center gap-1">
                      <AlertCircle size={10} /> Payment Pending
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage Breakdown Card */}
        {((usageStats?.usageByWorkPill?.length || 0) > 0 || (usageStats?.usageByProject?.length || 0) > 0) && (
          <Card className="border border-slate-200 shadow-sm overflow-hidden col-span-1 md:col-span-2">
            <div className="p-5">
              <div className="flex items-center justify-between mb-6">
                <div className="flex bg-slate-100 p-1 rounded-lg">
                  <button 
                    onClick={() => setActiveUsageTab('workpills')}
                    className={cn(
                      "px-4 py-1.5 text-xs font-medium rounded-md transition-all",
                      activeUsageTab === 'workpills' ? "bg-white shadow-sm text-blue-600" : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    By WorkPill
                  </button>
                  <button 
                    onClick={() => setActiveUsageTab('projects')}
                    className={cn(
                      "px-4 py-1.5 text-xs font-medium rounded-md transition-all",
                      activeUsageTab === 'projects' ? "bg-white shadow-sm text-blue-600" : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    By Project
                  </button>
                </div>
              </div>
              
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-2.5 text-left">{activeUsageTab === 'workpills' ? 'WorkPill Name' : 'Project Name'}</th>
                      <th className="px-4 py-2.5 text-left">Executions</th>
                      <th className="px-4 py-2.5 text-left">Cost</th>
                      <th className="px-4 py-2.5 text-right">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(activeUsageTab === 'workpills' ? usageStats?.usageByWorkPill : usageStats?.usageByProject)?.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{item.name}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-600">{item.executions_count.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-600">{formatCurrency(item.credits_used, currency)}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-900 font-bold text-right">{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        )}

        {/* Usage History Card */}
        <Card className="border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Clock size={20} className="text-slate-600" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">Usage History</h3>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto max-h-[400px]">
            {!usageStats?.usageByMessage || usageStats.usageByMessage.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={32} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No usage records found</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Model</th>
                    <th className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {usageStats.usageByMessage.map((item: any, idx) => (
                    <tr key={item.messageId || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatDateShort(new Date(item.date))}
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-900">
                        <div className="flex flex-col">
                          <span>{item.display_model_name || item.model}</span>
                          <span className="text-[10px] text-slate-400 font-normal">{item.project_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-bold text-slate-900 text-right">
                        {formatCurrency(item.cost, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* Alert Banner for Plan Limit */}
      {planUsagePercentage >= 95 && !isMonthlyUnlimited && currentOverageSpend === 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={18} />
          <div>
            <h4 className="font-bold text-red-900 text-sm">Plan Limit Reached</h4>
            <p className="text-xs text-red-800 mt-1">
              You have used all your subscription credits. Increase your Overage Limit above to continue using AI services, or wait for the next billing cycle.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIUsagePanel;
